<script setup>
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import {Head, usePage} from '@inertiajs/vue3'
import _ from 'lodash'

import ContactDialog from '../components/ui-components/ContactDialog.vue'
import PoweredByRoori from '../../administration/components/ui-components/PoweredByRoori.vue'
import RentalCheckoutDrawer from '../components/wizard/RentalCheckoutDrawer.vue'
import RentalItemDetail from '../components/ui-components/RentalItemDetail.vue'
import RentalWidgetHeader from '../components/ui-components/RentalWidgetHeader.vue'
import Slider from 'primevue/slider'
import {percentToHex} from '@/helpers.js'
import {trans, transChoice} from "laravel-vue-i18n";

const _page = usePage()

const props = defineProps({
    forms: Object,
    registrationForm: Object,
})

// ── Constants ─────────────────────────────────────────────────────────────────

// ── Locale ────────────────────────────────────────────────────────────────────

const intlLocale = computed(() => {
    const l = props.forms.locale ?? 'cs'
    // Normalise underscore form (cs_CZ → cs-CZ) and strip unknown suffixes
    return l.replace('_', '-')
})

const firstDayOfWeek = computed(() => {
    try {
        const info = new Intl.Locale(intlLocale.value).weekInfo
        if (info?.firstDay !== undefined) return info.firstDay  // 0=Sun, 1=Mon, 6=Sat
    } catch {}
    // Fallback: Sunday-first locales
    const sundayFirst = ['en', 'zh', 'ja', 'ko', 'ar', 'fa', 'he']
    const base = intlLocale.value.split('-')[0]
    return sundayFirst.includes(base) ? 0 : 1
})

const MONTHS = computed(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale.value, { month: 'long' })
    return Array.from({ length: 12 }, (_, i) => {
        const s = fmt.format(new Date(2024, i, 1))
        return s.charAt(0).toUpperCase() + s.slice(1)
    })
})

const DOW = computed(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short' })
    // Build 7 entries starting from firstDayOfWeek
    return Array.from({ length: 7 }, (_, i) => {
        const dow = (firstDayOfWeek.value + i) % 7
        // 2024-01-07 is a Sunday (0); offset accordingly
        const date = new Date(2024, 0, 7 + dow)
        const s = fmt.format(date)
        return s.charAt(0).toUpperCase() + s.slice(1)
    })
})

// ── Data ──────────────────────────────────────────────────────────────────────

const rentalItems = computed(() => _page.props.rentalItems ?? [])
const isSingleItem = computed(() => rentalItems.value.length === 1)

const DAY_NAME_TO_DOW = {
    monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0,
}

function blockedDaysOfType(type) {
    const items = rentalItems.value
    if (!items.length) return new Set()
    const blocked = new Set()
    for (let dow = 0; dow <= 6; dow++) {
        if (items.every(item =>
            item.booking_rules?.some(r => r.blocking_type === type && DAY_NAME_TO_DOW[r.day] === dow)
        )) blocked.add(dow)
    }
    return blocked
}

// Days of week (0-6) where ALL items block starting / ending / intersecting
const blockedStartDays = computed(() => blockedDaysOfType('start'))
const blockedEndDays = computed(() => blockedDaysOfType('end'))
const blockedIntersectDays = computed(() => blockedDaysOfType('intersect'))

function rangeContainsBlockedIntersect(startDate, endDate) {
    if (!blockedIntersectDays.value.size) return false
    const days = Math.round((endDate - startDate) / 86400000)
    if (days >= 7) return true
    for (let i = 1; i < days; i++) {
        const d = new Date(startDate.getTime() + i * 86400000)
        if (blockedIntersectDays.value.has(d.getDay())) return true
    }
    return false
}

// ── Theme ─────────────────────────────────────────────────────────────────────

const darkMode = ref(false)

watch(() => props.forms.theme?.mode_lock, (val) => {
    if (val === 'auto') {
        if (!window.matchMedia) return
        const query = window.matchMedia('(prefers-color-scheme: dark)')
        darkMode.value = query.matches
        query.matches
            ? document.querySelector('html')?.classList.add('dark')
            : document.querySelector('html')?.classList.remove('dark')
        query.addEventListener('change', (e) => {
            darkMode.value = e.matches
            e.matches
                ? document.querySelector('html')?.classList.add('dark')
                : document.querySelector('html')?.classList.remove('dark')
        })
    } else {
        val === 'light'
            ? document.querySelector('html')?.classList.remove('dark')
            : document.querySelector('html')?.classList.add('dark')
        darkMode.value = val === 'dark'
    }
}, {immediate: true})

watch(darkMode, (val) => {
    val
        ? document.querySelector('html')?.classList.add('dark')
        : document.querySelector('html')?.classList.remove('dark')
})

const hasBgImage = computed(() => {
    const t = props.forms.theme
    if (!t) return false
    return !!(darkMode.value ? t.background_image_url_dark : t.background_image_url)
})

const themePageStyle = computed(() => {
    const t = props.forms.theme
    if (!t) return {}
    const isDark = darkMode.value

    const pc   = isDark ? t.panel_color_dark        : t.panel_color
    const pt   = isDark ? t.panel_transparency_dark  : t.panel_transparency
    const blur = isDark ? t.panel_backdrop_blur_dark  : t.panel_backdrop_blur
    const bg   = isDark ? t.background_color_dark    : t.background_color
    const bgImg = isDark ? t.background_image_url_dark : t.background_image_url

    const rMap = {
        0: ['0px',  '0px',  '0px',  '0px'],
        1: ['4px',  '8px',  '12px', '16px'],
        2: ['8px',  '12px', '18px', '24px'],
        3: ['12px', '18px', '26px', '32px'],
    }
    const r = rMap[t.roundness ?? 2] ?? rMap[2]

    return {
        '--brand':           t.color_html ?? undefined,
        '--rw-panel-bg':     pc   ? `#${pc}${percentToHex(pt ?? 100)}` : undefined,
        '--rw-panel-blur':   blur ? '20px' : '0px',
        '--rw-r-sm':  r[0],
        '--rw-r-md':  r[1],
        '--rw-r-lg':  r[2],
        '--rw-r-xl':  r[3],
        backgroundColor: bg    ? `#${bg}`        : undefined,
        backgroundImage: bgImg ? `url(${bgImg})` : undefined,
    }
})

const formInitials = computed(() => {
    const name = props.forms.name ?? ''
    return name.split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase() || 'R'
})

const a11yStyle = ref({})

const wizardTheme = computed(() => ({
    color: props.forms.theme?.color ?? 'blue',
    roundness: props.forms.theme?.roundness ?? 2,
    darkMode: darkMode.value,
    borderless: props.forms.theme?.borderless ?? false,
    transparent: props.forms.theme?.transparent ?? false,
    rows: props.forms.theme?.rows ?? 1,
}))

// ── Prices & filters ──────────────────────────────────────────────────────────

const searchQuery = ref('')
const favorites = ref({})

const priceMin = computed(() => {
    const prices = rentalItems.value.map(i => i.prices?.[0]?.unit_price ?? 0).filter(p => p > 0)
    return prices.length ? Math.min(...prices) : 0
})
const priceMax = computed(() => {
    const prices = rentalItems.value.map(i => i.prices?.[0]?.unit_price ?? 0).filter(p => p > 0)
    return prices.length ? Math.max(...prices) : 999999
})

const priceRange = ref([0, 999999])
watch([priceMin, priceMax], ([min, max]) => {
    priceRange.value = [min, max]
}, {immediate: true})

const priceHistogram = computed(() => {
    const n = 24
    if (priceMax.value <= priceMin.value) return Array(n).fill(10)
    const span = priceMax.value - priceMin.value
    const buckets = Array(n).fill(0)
    rentalItems.value.forEach(item => {
        const p = item.prices?.[0]?.unit_price ?? 0
        if (p > 0) {
            const idx = Math.min(n - 1, Math.floor((p - priceMin.value) / span * n))
            buckets[idx]++
        }
    })
    const maxCount = Math.max(1, ...buckets)
    return buckets.map(b => Math.max(3, Math.round((b / maxCount) * 48)))
})

function histBucketActive(i) {
    if (priceMax.value <= priceMin.value) return true
    const n = priceHistogram.value.length
    const span = priceMax.value - priceMin.value
    const bMin = (priceRange.value[0] - priceMin.value) / span * n
    const bMax = (priceRange.value[1] - priceMin.value) / span * n
    return i + 0.5 >= bMin && i + 0.5 <= bMax
}

// ── Calendar helpers ──────────────────────────────────────────────────────────

function sameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function toYMD(d) {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
}

function buildCalCells(year, month) {
    const first = new Date(year, month, 1)
    const fdow = firstDayOfWeek.value
    const firstDow = (first.getDay() - fdow + 7) % 7
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells = []
    const prevDaysInMonth = new Date(year, month, 0).getDate()
    for (let i = 0; i < firstDow; i++) {
        cells.push({d: new Date(year, month - 1, prevDaysInMonth - firstDow + i + 1), out: true})
    }
    for (let i = 1; i <= daysInMonth; i++) {
        cells.push({d: new Date(year, month, i), out: false})
    }
    let nextDay = 1
    while (cells.length % 7 !== 0) {
        cells.push({d: new Date(year, month + 1, nextDay++), out: true})
    }
    return cells
}

function getCellClass(cell, hoveredRef) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const d = cell.d
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    const e = selectedDates.value?.end ? new Date(selectedDates.value.end) : null
    const effectiveEnd = e || (s && hoveredRef.value ? hoveredRef.value : null)

    let inRange = false
    if (s && effectiveEnd) {
        const min = s <= effectiveEnd ? s : effectiveEnd
        const max = s <= effectiveEnd ? effectiveEnd : s
        inRange = d > min && d < max
    }

    const isPickingStart = !s || (s && e)
    const isPickingEnd = !!(s && !e)
    const isBlockedEnd = isPickingEnd && d > s && (
        blockedEndDays.value.has(d.getDay()) || rangeContainsBlockedIntersect(s, d)
    )
    return {
        'rw-cal-cell': true,
        'out': cell.out,
        'disabled': d < today,
        'blocked-start': !cell.out && d >= today && isPickingStart && blockedStartDays.value.has(d.getDay()),
        'blocked-end': !cell.out && d >= today && isBlockedEnd,
        'range-start': !cell.out && s && sameDay(d, s) && !(e && sameDay(d, e)),
        'range-end': !cell.out && e && sameDay(d, e) && !(s && sameDay(d, s)),
        'range-single': !cell.out && s && e && sameDay(d, s) && sameDay(d, e),
        'in-range': inRange && !(s && sameDay(d, s)) && !(e && sameDay(d, e)),
        'today': !cell.out && sameDay(d, today),
        'busy-full': !cell.out && busyDaysData.value[toYMD(d)] === 'full',
        'busy-partial': !cell.out && busyDaysData.value[toYMD(d)] === 'partial',
    }
}

function handleCalPick(cell) {
    if (cell.out) return
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (cell.d < today) return
    const d = cell.d
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    const e = selectedDates.value?.end ? new Date(selectedDates.value.end) : null
    if (!s || (s && e) || d < s) {
        if (blockedStartDays.value.has(d.getDay())) return
        selectedDates.value = {start: toYMD(d), end: null}
    } else if (d > s && (blockedEndDays.value.has(d.getDay()) || rangeContainsBlockedIntersect(s, d))) {
        return
    } else if (sameDay(d, s)) {
        const tomorrow = new Date(d)
        tomorrow.setDate(d.getDate() + 1)
        selectedDates.value = {start: toYMD(d), end: toYMD(tomorrow)}
    } else {
        selectedDates.value = {start: selectedDates.value.start, end: toYMD(d)}
    }
}

function fmtDateShort(d) {
    return d.toLocaleDateString(intlLocale.value, {day: 'numeric', month: 'short'})
}

function fmtKc(amount, currency = 'CZK') {
    return new Intl.NumberFormat(intlLocale.value, {style: 'currency', currency, minimumFractionDigits: 0}).format(amount)
}

// ── Filter bar popover state ───────────────────────────────────────────────────

const openPopover = ref(null)
const filterWrapRef = ref(null)
const calViewLeft = ref({y: new Date().getFullYear(), m: new Date().getMonth()})
const calHovered = ref(null)

const calViewRight = computed(() => {
    let m = calViewLeft.value.m + 1, y = calViewLeft.value.y
    if (m > 11) {m = 0; y++}
    return {y, m}
})

function calShift(n) {
    let m = calViewLeft.value.m + n, y = calViewLeft.value.y
    while (m < 0) {m += 12; y--}
    while (m > 11) {m -= 12; y++}
    calViewLeft.value = {y, m}
}

const calCellsLeft = computed(() => buildCalCells(calViewLeft.value.y, calViewLeft.value.m))
const calCellsRight = computed(() => buildCalCells(calViewRight.value.y, calViewRight.value.m))

function calCellClass(cell) {
    return getCellClass(cell, calHovered)
}

function getCellTooltip(cell) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (cell.out || cell.d < today) return null
    const d = cell.d
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    const e = selectedDates.value?.end ? new Date(selectedDates.value.end) : null
    const isPickingStart = !s || (s && e)
    const isPickingEnd = !!(s && !e)

    if (isPickingStart && blockedStartDays.value.has(d.getDay())) {
        return trans('widget.blocked_start_day')
    }
    if (isPickingEnd && d > s) {
        if (blockedEndDays.value.has(d.getDay())) return trans('widget.blocked_end_day')
        if (rangeContainsBlockedIntersect(s, d)) return trans('widget.blocked_intersect_day')
    }
    return null
}

function calCellTooltip(cell) { return getCellTooltip(cell) }

function calSetHover(cell) {
    if (cell.out) return
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (cell.d >= today) calHovered.value = cell.d
}

function togglePopover(name) {
    openPopover.value = openPopover.value === name ? null : name
}

function closeAllPopovers() {
    openPopover.value = null
    calHovered.value = null
}

function handleOutsideClick(e) {
    if (filterWrapRef.value && !filterWrapRef.value.contains(e.target)) {
        closeAllPopovers()
    }
}

// ── Dates & availability ──────────────────────────────────────────────────────

const selectedDates = ref(null)
const availabilityData = ref(null)
const isLoadingAvailability = ref(false)

const nightsDiff = computed(() => {
    if (!selectedDates.value?.start || !selectedDates.value?.end) return 0
    return Math.round((new Date(selectedDates.value.end) - new Date(selectedDates.value.start)) / 86400000)
})

const selectedDatesLabel = computed(() => {
    if (!selectedDates.value?.start) return 'Vyberte termín'
    if (!selectedDates.value?.end) return `${fmtDateShort(new Date(selectedDates.value.start))} → ?`
    return `${fmtDateShort(new Date(selectedDates.value.start))} – ${fmtDateShort(new Date(selectedDates.value.end))}`
})

const fetchAvailability = _.debounce(async (start, end) => {
    if (!start || !end) {
        availabilityData.value = null
        return
    }
    availabilityData.value = null
    isLoadingAvailability.value = true
    try {
        const attendees = isAccommodation.value ? (guestFilter.value.adults + guestFilter.value.children) : 1
        const res = await axios.get(`/api/rental/available/${props.forms.slug}/${start}/${end}/${attendees}`)
        availabilityData.value = Object.fromEntries(res.data.map(item => [item.id, item]))
    } catch {
        availabilityData.value = null
    } finally {
        isLoadingAvailability.value = false
    }
}, 500)

watch(selectedDates, (val) => {
    fetchAvailability(val?.start, val?.end)
})

// ── Busy days (calendar occupancy indicators) ─────────────────────────────

const busyDaysData = ref({})

async function doBusyDaysFetch({y, m}) {
    const start = `${y}-${String(m + 1).padStart(2, '0')}-01`
    const endY = m >= 11 ? y + 1 : y
    const endM = m >= 11 ? 0 : m + 1
    const lastDay = new Date(endY, endM + 1, 0).getDate()
    const end = `${endY}-${String(endM + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    const res = await axios.get(`/api/rental/busy-days/${props.forms.slug}/${start}/${end}`)
    busyDaysData.value = {...busyDaysData.value, ...res.data}
}

const fetchBusyDays = _.debounce(async ({y, m}) => {
    try { await doBusyDaysFetch({y, m}) } catch {}
}, 300)

watch(calViewLeft, (val) => fetchBusyDays(val), {immediate: true})

const isRefreshingCalendar = ref(false)

async function refreshCalendar() {
    if (isRefreshingCalendar.value) return
    busyDaysData.value = {}
    isRefreshingCalendar.value = true
    try {
        const monthsToFetch = [calViewLeft.value, calViewRight.value]
        const promises = monthsToFetch.map(v => doBusyDaysFetch(v))
        if (selectedDates.value?.start && selectedDates.value?.end) {
            const attendees = isAccommodation.value ? (guestFilter.value.adults + guestFilter.value.children) : 1
            promises.push(
                axios.get(`/api/rental/available/${props.forms.slug}/${selectedDates.value.start}/${selectedDates.value.end}/${attendees}`)
                    .then(res => { availabilityData.value = Object.fromEntries(res.data.map(item => [item.id, item])) })
            )
        }
        await Promise.all(promises)
    } catch {} finally {
        isRefreshingCalendar.value = false
    }
}

const isUnavailable = (item) => availabilityData.value !== null && !availabilityData.value[item.id]
const getComputedPrice = (item) => availabilityData.value?.[item.id]?.computedPrice ?? null

// ── Filtered grid ─────────────────────────────────────────────────────────────

const filteredItems = computed(() => rentalItems.value.filter(item => {
    if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase()
        if (!item.name?.toLowerCase().includes(q) && !item.description?.toLowerCase().includes(q)) return false
    }
    const basePrice = item.prices?.[0]?.unit_price ?? 0
    if (basePrice < priceRange.value[0] || basePrice > priceRange.value[1]) return false

    const totalGuests = guestFilter.value.adults + guestFilter.value.children
    if (item.max_pop != null && totalGuests > item.max_pop) return false
    if (item.max_adults != null && guestFilter.value.adults > item.max_adults) return false
    if (item.max_children != null && guestFilter.value.children > item.max_children) return false
    if (item.max_pets === 0 && guestFilter.value.pets > 0) return false
    if (item.max_pets != null && item.max_pets > 0 && guestFilter.value.pets > item.max_pets) return false

    return true
}))

const activeFilters = computed(() => {
    const chips = []
    if (selectedDates.value?.start && selectedDates.value?.end) {
        chips.push({key: 'dates', label: selectedDatesLabel.value, clear: () => { selectedDates.value = null; availabilityData.value = null }})
    }
    if (priceRange.value[0] > priceMin.value || priceRange.value[1] < priceMax.value) {
        chips.push({key: 'price', label: `${fmtKc(priceRange.value[0])} – ${fmtKc(priceRange.value[1])}`, clear: () => { priceRange.value = [priceMin.value, priceMax.value] }})
    }
    if (searchQuery.value.trim()) {
        chips.push({key: 'q', label: `"${searchQuery.value.trim()}"`, clear: () => { searchQuery.value = '' }})
    }
    if (guestFilterActive.value) {
        chips.push({key: 'guests', label: guestFilterLabel.value, clear: () => { guestFilter.value = { adults: 1, children: 0, pets: 0 } }})
    }
    return chips
})

// ── Card helpers ──────────────────────────────────────────────────────────────

function itemPlaceholderColors(item) {
    const h1 = (item.id * 67 + 180) % 360
    const h2 = (item.id * 67 + 220) % 360
    const dark = item.id % 3 === 0
    return {
        c1: `oklch(${dark ? 0.32 : 0.78} 0.07 ${h1})`,
        c2: `oklch(${dark ? 0.22 : 0.66} 0.06 ${h2})`,
        c3: `oklch(${dark ? 0.16 : 0.58} 0.05 ${h2})`,
        gradId: `rw-grad-${item.id}`,
        patId: `rw-pat-${item.id}`,
    }
}

function itemBasePrice(item) {
    return item.prices?.[0]?.unit_price ?? 0
}

function itemDisplayPrice(item) {
    const cp = getComputedPrice(item)
    return cp ? cp.final_price : itemBasePrice(item)
}

const UNIT_LABELS = {
    hour: 'hod.', half_day: '½ den', day: 'den', night: 'noc',
    week: 'týd.', half_month: '½ měs.', month: 'měs.', quarter: 'kvartál',
    year: 'rok', weekend: 'víkend', accommodation: 'noc',
}

function itemUnit(item) {
    return UNIT_LABELS[item?.unit] ?? trans(item?.unit ?? 'night')
}

const formIsSingleDay = computed(() =>
    rentalItems.value.length > 0 &&
    rentalItems.value.every(i => ['hour', 'half_day'].includes(i.unit))
)

const isAccommodation = computed(() => props.forms.form_type?.name === 'accommodation')

// ── Guest filter (main listing) ───────────────────────────────────────────────

const guestFilter = ref({ adults: 1, children: 0, pets: 0 })

const guestFilterActive = computed(() =>
    guestFilter.value.adults > 1 || guestFilter.value.children > 0 || guestFilter.value.pets > 0
)

const guestFilterLabel = computed(() => {
    const total = guestFilter.value.adults + guestFilter.value.children
    const parts = [`${total} host${total === 1 ? '' : 'é'}`]
    if (guestFilter.value.pets > 0) parts.push(`${guestFilter.value.pets} mazl.`)
    return parts.join(' · ')
})

// ── Item detail modal ─────────────────────────────────────────────────────────

const pageRef = ref(null)

const detailItem = ref(null)
const detailVisible = ref(false)

function openDetail(item) {
    detailItem.value = item
    detailVisible.value = true
}

function closeDetail() {
    detailVisible.value = false
    detailItem.value = null
}

const sigUnitLabel = computed(() => {
    const units = [...new Set(rentalItems.value.map(i => i.unit).filter(Boolean))]
    if (units.length === 1) return UNIT_LABELS[units[0]] ?? units[0]
    return 'jedn.'
})

// ── Signature panel meta ──────────────────────────────────────────────────────

const sigMeta = computed(() => {
    const prices = rentalItems.value.map(i => i.prices?.[0]?.unit_price ?? 0).filter(p => p > 0)
    return {
        count: rentalItems.value.length,
        fromPrice: prices.length ? Math.min(...prices) : 0,
        response: '< 1 h',
        rating: props.forms.rating ? props.forms.rating.toFixed(2) : '—',
    }
})

// ── Contact dialog ────────────────────────────────────────────────────────────

const contactDialogModel = ref(false)

// ── Checkout ──────────────────────────────────────────────────────────────────

const checkoutVisible = ref(false)
const checkoutItem = ref(null)

function handleBook(item) {
    if (isUnavailable(item)) return
    checkoutItem.value = item
    checkoutVisible.value = true
}

function onKeyDown(e) {
    if (e.key === 'Escape') {
        if (detailVisible.value) { closeDetail(); return }
        closeAllPopovers()
    }
}

onMounted(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', onKeyDown)
})
onUnmounted(() => {
    document.removeEventListener('mousedown', handleOutsideClick)
    document.removeEventListener('keydown', onKeyDown)
})
</script>

<template>
    <div ref="pageRef" class="rw-page" :class="{'is-dark': darkMode}" :style="{ ...themePageStyle, ...a11yStyle }">
        <Head :title="forms.name"></Head>

        <!-- Ambient CSS background — reaguje na --brand -->
        <div class="rw-ambient" :style="{ opacity: hasBgImage ? 0.8 : 1 }" aria-hidden="true"/>

        <!-- Brand tint overlay — jemný nádech accent coloru vždy přítomný -->
        <div class="rw-brand-overlay" aria-hidden="true"/>

        <!-- Single item: full-page Airbnb-style detail view -->
        <rental-item-detail
            v-if="isSingleItem"
            :item="rentalItems[0]"
            :forms="forms"
            :initial-dates="selectedDates"
            :initial-guests="guestFilter"
            :wizard-theme="wizardTheme"
            :dark-mode="darkMode"
            @contact="contactDialogModel = true"
        />

        <template v-else>

        <div class="rw-inner">
            <!-- ── Header ──────────────────────────────────────────────── -->
            <rental-widget-header
                :forms="forms"
                :dark-mode="darkMode"
                v-model:a11y-style="a11yStyle"
                @toggle-dark="darkMode = !darkMode"
            >
                <button class="rw-hdr-link" @click="contactDialogModel = true">
                    <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="m2 7 10 8 10-8"/>
                    </svg>
                    Kontakt
                </button>
                <span class="rw-hdr-divider"/>
                <div class="rw-avatar">{{ formInitials }}</div>
            </rental-widget-header>

            <!-- ── Signature panel ────────────────────────────────────── -->
            <section class="rw-sig rw-shell">
                <div class="rw-sig-frame">
                    <div class="rw-sig-content">
                        <div class="rw-sig-tag">REZERVAČNÍ KANÁL · ROORI</div>
                        <h1 class="rw-sig-title">{{ forms.name }}</h1>
                        <div v-if="forms.description" class="rw-sig-sub" v-html="forms.description"/>
                        <div class="rw-sig-meta">
                            <div class="rw-sig-cell">
                                <span class="rw-sig-lbl">Nabídek</span>
                                <span class="rw-sig-val">{{ sigMeta.count }}</span>
                            </div>
                            <div class="rw-sig-cell">
                                <span class="rw-sig-lbl">Od ceny</span>
                                <span class="rw-sig-val">{{ sigMeta.fromPrice ? fmtKc(sigMeta.fromPrice) : '—' }}<small> /{{ sigUnitLabel }}</small></span>
                            </div>
                            <div class="rw-sig-cell">
                                <span class="rw-sig-lbl">Odezva</span>
                                <span class="rw-sig-val">{{ sigMeta.response }}</span>
                            </div>
                            <div class="rw-sig-cell">
                                <span class="rw-sig-lbl">Hodnocení</span>
                                <span class="rw-sig-val">{{ sigMeta.rating }} <small>★</small></span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ── Filter bar ─────────────────────────────────────────── -->
            <div class="rw-shell rw-filter-shell">
                <div class="rw-filter-wrap" ref="filterWrapRef">
                    <div class="rw-filter" :style="isAccommodation ? 'grid-template-columns:1.5fr 1fr 1.2fr 1fr auto' : 'grid-template-columns:1.5fr 1.2fr 1fr auto'">
                        <!-- Termín field – hidden for hourly/half_day forms -->
                        <button
                            v-if="!formIsSingleDay"
                            class="rw-fld"
                            :class="{'is-active': openPopover === 'dates'}"
                            @click="togglePopover('dates')"
                        >
                            <div class="rw-fld-label">
                                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="5" width="18" height="16" rx="2"/>
                                    <path d="M3 9h18M8 3v4M16 3v4"/>
                                </svg>
                                Termín
                            </div>
                            <div class="rw-fld-value" :class="{'is-placeholder': !selectedDates?.start}">
                                {{ selectedDatesLabel }}
                                <span v-if="isLoadingAvailability" class="rw-loading-dot"/>
                            </div>

                            <!-- Calendar popover -->
                            <div v-if="openPopover === 'dates'" class="rw-pop rw-pop-cal" :class="{'rw-pop-cal--refreshing': isRefreshingCalendar}" @click.stop @mousedown.stop>
                                <div style="flex:1" @mouseleave="calHovered = null">
                                    <div class="rw-cal-hd">
                                        <div class="rw-cal-nav">
                                            <button @click.stop="calShift(-1)" aria-label="Předchozí">
                                                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 6-6 6 6 6"/></svg>
                                            </button>
                                        </div>
                                        <b>{{ MONTHS[calViewLeft.m] }} {{ calViewLeft.y }}</b>
                                        <button class="rw-cal-nav-btn rw-cal-nav-btn--refresh" :disabled="isRefreshingCalendar" @click.stop="refreshCalendar" title="Aktualizovat">
                                            <svg class="rw-icon" :class="{'rw-spin': isRefreshingCalendar}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                                        </button>
                                    </div>
                                    <div class="rw-cal-grid">
                                        <div v-for="d in DOW" :key="d" class="rw-cal-dow">{{ d }}</div>
                                        <button
                                            v-for="(cell, i) in calCellsLeft"
                                            :key="i"
                                            :class="calCellClass(cell)"
                                            @click.stop="handleCalPick(cell)"
                                            @mouseenter="calSetHover(cell)"
                                        >{{ cell.d.getDate() }}<span v-if="calCellTooltip(cell)" class="rw-cell-tip">{{ calCellTooltip(cell) }}</span></button>
                                    </div>
                                </div>
                                <div style="flex:1" @mouseleave="calHovered = null">
                                    <div class="rw-cal-hd">
                                        <div style="width:30px"/>
                                        <b>{{ MONTHS[calViewRight.m] }} {{ calViewRight.y }}</b>
                                        <div class="rw-cal-nav">
                                            <button @click.stop="calShift(1)" aria-label="Další">
                                                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="rw-cal-grid">
                                        <div v-for="d in DOW" :key="d" class="rw-cal-dow">{{ d }}</div>
                                        <button
                                            v-for="(cell, i) in calCellsRight"
                                            :key="i"
                                            :class="calCellClass(cell)"
                                            @click.stop="handleCalPick(cell)"
                                            @mouseenter="calSetHover(cell)"
                                        >{{ cell.d.getDate() }}<span v-if="calCellTooltip(cell)" class="rw-cell-tip">{{ calCellTooltip(cell) }}</span></button>
                                    </div>
                                </div>
                                <div class="rw-pop-cal-fd">
                                    <div class="rw-cal-nights">
                                        <template v-if="selectedDates?.start && selectedDates?.end">
                                            <b>{{ nightsDiff }} {{ isAccommodation ? transChoice('widget.nights', nightsDiff, {count: nightsDiff}) : transChoice('widget.days_count', nightsDiff, {count: nightsDiff}) }}</b>
                                            · {{ selectedDatesLabel }}
                                        </template>
                                        <template v-else-if="selectedDates?.start">
                                            Vybrán <b>{{ fmtDateShort(new Date(selectedDates.start)) }}</b> — vyberte konec
                                        </template>
                                        <span v-else style="color:var(--rw-ink-3)">Vyberte termín pro zobrazení dostupnosti</span>
                                    </div>
                                    <div style="display:flex;gap:6px">
                                        <button class="rw-chip" @click.stop="selectedDates = null; availabilityData = null">Vymazat</button>
                                        <button class="rw-chip rw-chip-on" @click.stop="closeAllPopovers()">Hotovo</button>
                                    </div>
                                </div>
                            </div>
                        </button>

                        <!-- Hosté field – jen pro accommodation -->
                        <button
                            v-if="isAccommodation"
                            class="rw-fld"
                            :class="{'is-active': openPopover === 'guests'}"
                            @click="togglePopover('guests')"
                        >
                            <div class="rw-fld-label">
                                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="8" cy="7" r="4"/>
                                    <path d="M2 21a6 6 0 0 1 12 0"/>
                                    <circle cx="17" cy="9" r="3"/>
                                    <path d="M22 21a4 4 0 0 0-7 0"/>
                                </svg>
                                Hosté
                            </div>
                            <div class="rw-fld-value" :class="{'is-placeholder': !guestFilterActive}">
                                {{ guestFilterActive ? guestFilterLabel : 'Přidat hosty' }}
                            </div>

                            <!-- Guest popover -->
                            <div v-if="openPopover === 'guests'" class="rw-pop rw-pop-guests" @click.stop @mousedown.stop>
                                <b style="font-size:1em;color:var(--rw-ink)">Hosté</b>
                                <div class="rw-guests" style="margin-top:16px">
                                    <div class="rw-guest-row">
                                        <div>
                                            <div class="rw-guest-lbl">Dospělí</div>
                                            <div class="rw-guest-sub">13+ let</div>
                                        </div>
                                        <div class="rw-counter">
                                            <button @click.stop="guestFilter.adults = Math.max(1, guestFilter.adults - 1)" :disabled="guestFilter.adults <= 1">−</button>
                                            <span>{{ guestFilter.adults }}</span>
                                            <button @click.stop="guestFilter.adults++">+</button>
                                        </div>
                                    </div>
                                    <div class="rw-guest-row">
                                        <div>
                                            <div class="rw-guest-lbl">Děti</div>
                                            <div class="rw-guest-sub">0–12 let</div>
                                        </div>
                                        <div class="rw-counter">
                                            <button @click.stop="guestFilter.children = Math.max(0, guestFilter.children - 1)" :disabled="guestFilter.children <= 0">−</button>
                                            <span>{{ guestFilter.children }}</span>
                                            <button @click.stop="guestFilter.children++">+</button>
                                        </div>
                                    </div>
                                    <div class="rw-guest-row">
                                        <div>
                                            <div class="rw-guest-lbl">Mazlíčci</div>
                                        </div>
                                        <div class="rw-counter">
                                            <button @click.stop="guestFilter.pets = Math.max(0, guestFilter.pets - 1)" :disabled="guestFilter.pets <= 0">−</button>
                                            <span>{{ guestFilter.pets }}</span>
                                            <button @click.stop="guestFilter.pets++">+</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </button>

                        <!-- Cena field -->
                        <button
                            class="rw-fld"
                            :class="{'is-active': openPopover === 'price'}"
                            @click="togglePopover('price')"
                        >
                            <div class="rw-fld-label">
                                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="12" cy="12" r="9"/>
                                    <path d="M14.5 9.5h-3a2 2 0 0 0 0 4h1a2 2 0 1 1 0 4h-3M12 7v2M12 15v2"/>
                                </svg>
                                Cena za {{ sigUnitLabel }}
                            </div>
                            <div class="rw-fld-value" :class="{'is-placeholder': priceRange[0] <= priceMin && priceRange[1] >= priceMax}">
                                {{ priceRange[0] > priceMin || priceRange[1] < priceMax
                                    ? `${fmtKc(priceRange[0])} – ${fmtKc(priceRange[1])}`
                                    : 'Jakákoliv cena' }}
                            </div>

                            <!-- Price popover -->
                            <div v-if="openPopover === 'price'" class="rw-pop rw-pop-price" @click.stop @mousedown.stop>
                                <div class="rw-price-pop-h">
                                    <b>Rozsah ceny</b>
                                    <span>{{ fmtKc(priceRange[0]) }} – {{ fmtKc(priceRange[1]) }} / {{ sigUnitLabel }}</span>
                                </div>
                                <div class="rw-price-hist">
                                    <div
                                        v-for="(h, i) in priceHistogram"
                                        :key="i"
                                        class="rw-price-bar"
                                        :class="{active: histBucketActive(i)}"
                                        :style="{height: h + 'px'}"
                                    />
                                </div>
                                <Slider v-model="priceRange" range :min="priceMin" :max="priceMax" :step="100" class="rw-price-slider"/>
                                <div class="rw-price-inputs">
                                    <div class="rw-price-input">
                                        <label>Min</label>
                                        <div class="rw-price-input-row">
                                            <input
                                                type="number"
                                                :value="priceRange[0]"
                                                @input="priceRange = [Math.max(priceMin, Math.min(+$event.target.value, priceRange[1] - 100)), priceRange[1]]"
                                            />
                                            <span>Kč</span>
                                        </div>
                                    </div>
                                    <div class="rw-price-input">
                                        <label>Max</label>
                                        <div class="rw-price-input-row">
                                            <input
                                                type="number"
                                                :value="priceRange[1]"
                                                @input="priceRange = [priceRange[0], Math.max(priceRange[0] + 100, Math.min(+$event.target.value, priceMax))]"
                                            />
                                            <span>Kč</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </button>

                        <!-- Search field -->
                        <div class="rw-fld">
                            <div class="rw-fld-label">
                                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="11" cy="11" r="7"/>
                                    <path d="m20 20-3.5-3.5"/>
                                </svg>
                                Hledat
                            </div>
                            <input
                                v-model="searchQuery"
                                class="rw-fld-value rw-fld-input"
                                placeholder="Název, popis…"
                                @click.stop
                                @mousedown.stop
                            />
                        </div>

                        <!-- Search button -->
                        <button class="rw-btn-search" @click="closeAllPopovers()">
                            <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="11" cy="11" r="7"/>
                                <path d="m20 20-3.5-3.5"/>
                            </svg>
                            Hledat
                        </button>
                    </div>
                </div>
            </div>

            <!-- ── Results ─────────────────────────────────────────────── -->
            <main class="rw-shell rw-main">
                <div class="rw-results-head">
                    <div>
                        <div class="rw-eyebrow">Vyberte z nabídky</div>
                        <h2 class="rw-results-h2">
                            {{ filteredItems.length }}
                            {{ filteredItems.length === 1 ? 'položka' : (filteredItems.length <= 4 ? 'položky' : 'položek') }}
                            <small v-if="isLoadingAvailability">· kontroluji dostupnost…</small>
                        </h2>
                    </div>
                    <div class="rw-results-tools">
                        <button
                            v-for="f in activeFilters"
                            :key="f.key"
                            class="rw-chip rw-chip-on"
                            @click="f.clear()"
                        >
                            {{ f.label }}
                            <span class="rw-chip-x">×</span>
                        </button>
                    </div>
                </div>

                <!-- Empty state -->
                <div v-if="filteredItems.length === 0" class="rw-empty">
                    <div class="rw-eyebrow">Žádné výsledky</div>
                    <h3 style="margin-top:10px">Pro tyto filtry nemáme žádnou nabídku</h3>
                    <p>Zkuste rozšířit cenové rozpětí nebo změňte hledaný výraz.</p>
                    <button class="rw-chip rw-chip-on" style="margin-top:8px" @click="searchQuery = ''; priceRange = [priceMin, priceMax]">
                        Zrušit filtry
                    </button>
                </div>

                <!-- Item grid -->
                <div v-else class="rw-grid" :class="{'rw-grid-loading': isLoadingAvailability}">
                    <div
                        v-for="item in filteredItems"
                        :key="item.id"
                        class="rw-card"
                        :class="{'is-unavailable': availabilityData && isUnavailable(item)}"
                    >
                        <div class="rw-card-photo" style="cursor:pointer" @click="openDetail(item)">
                            <img
                                v-if="item.gallery?.[0]?.full_url"
                                :src="item.gallery[0].full_url"
                                :alt="item.name"
                                class="rw-card-img"
                            />
                            <svg v-else viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:100%">
                                <defs>
                                    <linearGradient :id="itemPlaceholderColors(item).gradId" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0" :stop-color="itemPlaceholderColors(item).c1"/>
                                        <stop offset="0.6" :stop-color="itemPlaceholderColors(item).c2"/>
                                        <stop offset="1" :stop-color="itemPlaceholderColors(item).c3"/>
                                    </linearGradient>
                                    <pattern :id="itemPlaceholderColors(item).patId" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
                                        <line x1="0" y1="0" x2="0" y2="14" stroke="rgba(255,255,255,0.045)" stroke-width="6"/>
                                    </pattern>
                                </defs>
                                <rect width="400" height="300" :fill="`url(#${itemPlaceholderColors(item).gradId})`"/>
                                <rect width="400" height="300" :fill="`url(#${itemPlaceholderColors(item).patId})`"/>
                                <line x1="0" y1="190" x2="400" y2="172" stroke="rgba(255,255,255,0.10)" stroke-width="1"/>
                                <line x1="0" y1="220" x2="400" y2="240" stroke="rgba(0,0,0,0.08)" stroke-width="1"/>
                                <text x="20" y="280" fill="rgba(255,255,255,0.55)"
                                      font-family="Syne, ui-sans-serif, sans-serif"
                                      font-size="9" letter-spacing="1.2">
                                    {{ item.name?.toUpperCase().slice(0, 30) }}
                                </text>
                            </svg>

                            <!-- Unavailable overlay -->
                            <div v-if="availabilityData && isUnavailable(item)" class="rw-unavail-overlay">
                                <span>Obsazeno</span>
                            </div>

                            <!-- Available badge with computed price -->
                            <div v-if="availabilityData && !isUnavailable(item) && getComputedPrice(item)" class="rw-avail-badge">
                                {{ fmtKc(getComputedPrice(item).final_price) }} celkem
                            </div>

                            <!-- Favorite heart -->
                            <button
                                class="rw-heart"
                                :class="{'is-on': favorites[item.id]}"
                                @click.stop="favorites = {...favorites, [item.id]: !favorites[item.id]}"
                                :aria-label="favorites[item.id] ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'"
                            >
                                <svg class="rw-icon" viewBox="0 0 24 24" :fill="favorites[item.id] ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                </svg>
                            </button>

                        </div>

                        <div class="rw-card-meta">
                            <div class="rw-card-loc">
                                <span class="rw-type-pill">
                                    {{ forms.form_type?.name === 'accommodation' ? 'Ubytování' : 'Pronájem' }}
                                </span>
                            </div>
                            <div class="rw-card-title" style="cursor:pointer" @click="openDetail(item)">{{ item.name }}</div>
                            <div class="rw-card-feats">
                                <template v-if="item.max_pop && isAccommodation">
                                    <span>
                                        <svg class="rw-icon" style="width:13px;height:13px;margin-right:3px;vertical-align:-2px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="8" r="4"/>
                                            <path d="M4 21a8 8 0 0 1 16 0"/>
                                        </svg>
                                        {{ item.max_pop }} os.
                                    </span>
                                    <span v-if="item.description" class="rw-dot"/>
                                </template>
                                <span v-if="item.description" class="rw-card-desc" v-html="item.description"/>
                            </div>
                            <div v-if="item.attributes?.some(a => a.is_highlighted && a.is_visible)" class="rw-card-attrs">
                                <span
                                    v-for="attr in item.attributes.filter(a => a.is_highlighted && a.is_visible).slice(0, 3)"
                                    :key="attr.label"
                                    class="rw-attr-pill"
                                >
                                    <i v-if="attr.icon" :class="attr.icon" style="font-size:.667em"/>
                                    {{ attr.label }}: {{ attr.value }}
                                </span>
                            </div>
                            <div class="rw-card-bot">
                                <div class="rw-card-price">
                                    <template v-if="availabilityData && getComputedPrice(item) && getComputedPrice(item).units > 1">
                                        <span class="rw-card-base">{{ fmtKc(itemBasePrice(item)) }} / {{ itemUnit(item) }}</span>
                                        <span>{{ fmtKc(getComputedPrice(item).final_price) }} <small>celkem</small></span>
                                    </template>
                                    <template v-else>
                                        {{ fmtKc(availabilityData && getComputedPrice(item) ? getComputedPrice(item).final_price : itemBasePrice(item)) }}
                                        <small>/ {{ itemUnit(item) }}</small>
                                    </template>
                                </div>
                                <button
                                    class="rw-book-btn"
                                    :disabled="availabilityData !== null && isUnavailable(item)"
                                    @click="handleBook(item)"
                                >
                                    Rezervovat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <!-- ── Micro footer ────────────────────────────────────────── -->
            <footer class="rw-footer">
                <div class="rw-shell rw-footer-inner">
                    <div class="rw-footer-links">
                        <a>Storno</a> · <a>Podmínky</a> · <a>GDPR</a> · <a @click="contactDialogModel = true" style="cursor:default">Kontakt</a>
                    </div>
                </div>
            </footer>
        </div>

        <powered-by-roori v-if="forms.show_banner"/>


        <!-- Detail overlay (multi-item mode) -->
        <Transition name="rw-detail">
            <div v-if="detailVisible" class="rw-detail-overlay">
                <rental-item-detail
                    :item="detailItem"
                    :is-overlay="true"
                    @close="closeDetail"
                    :forms="forms"
                    :initial-dates="selectedDates"
                    :initial-guests="guestFilter"
                    :wizard-theme="wizardTheme"
                    :dark-mode="darkMode"
                />
            </div>
        </Transition>

        <!-- Checkout drawer (grid-level Rezervovat button) -->
        <rental-checkout-drawer
            v-model="checkoutVisible"
            :item="checkoutItem"
            :forms="forms"
            :initial-dates="selectedDates"
            :initial-guests="guestFilter"
            :wizard-theme="wizardTheme"
        />
        </template>

    <!-- Contact dialog — mimo v-if/v-else, dostupný v obou módech -->
    <contact-dialog
        v-model:visible="contactDialogModel"
        :url="`/send-contact-form-anonymous/${forms.slug}`"
        extended
    />
    </div>
</template>

<style scoped>
/* ─── Design tokens ──────────────────────────────────────────────────────── */
.rw-page {
    --rw-ink: #0F1115;
    --rw-ink-2: #3A3F47;
    --rw-ink-3: #6B7280;
    --rw-ink-4: #9BA1AB;
    --rw-line: #E5E2DC;
    --rw-line-2: #EDEAE3;
    --rw-bg: #F7F7F4;
    --rw-bg-2: #FBFAF7;
    --rw-paper: #FFFFFF;
    --brand: #2A6FDB;
    --rw-brand-2: color-mix(in oklch, var(--brand) 80%, black);
    --rw-brand-soft: color-mix(in oklch, var(--brand) 18%, white);
    --rw-brand-tint: color-mix(in oklch, var(--brand) 10%, white);
    --rw-font-display: "Syne", ui-sans-serif, system-ui, sans-serif;
    --rw-font-body: "Syne", ui-sans-serif, system-ui, sans-serif;
    --rw-font-mono: "Syne", ui-sans-serif, system-ui, sans-serif;
    --rw-r-sm: 8px;
    --rw-r-md: 12px;
    --rw-r-lg: 18px;
    --rw-r-xl: 24px;

    font-family: var(--rw-font-body);
    font-size: 15px;
    line-height: 1.45;
    color: var(--rw-ink);
    background: var(--rw-bg);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    min-height: 100vh;
    position: relative;
    box-sizing: border-box;
}

.rw-page *, .rw-page *::before, .rw-page *::after {
    box-sizing: border-box;
}

/* Dark mode */
.rw-page.is-dark {
    --rw-ink: #F7F7F4;
    --rw-ink-2: #C8CDD6;
    --rw-ink-3: #8B92A0;
    --rw-ink-4: #5B616C;
    --rw-line: #22262E;
    --rw-line-2: #1A1E25;
    --rw-bg: #0F1115;
    --rw-bg-2: #16191F;
    --rw-paper: #1A1E25;
    --rw-brand-tint: color-mix(in oklch, var(--brand) 16%, transparent);
    --rw-brand-soft: color-mix(in oklch, var(--brand) 22%, transparent);
}

/* ─── Layout ─────────────────────────────────────────────────────────────── */
.rw-inner {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.rw-shell {
    max-width: 1320px;
    margin: 0 auto;
    padding: 0 32px;
    width: 100%;
}

/* ─── Ambient background ─────────────────────────────────────────────────── */
.rw-ambient {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
}

.rw-ambient {
    background:
        radial-gradient(ellipse 55% 45% at 15% 18%, color-mix(in oklch, var(--brand) 55%, transparent) 0%, transparent 100%),
        radial-gradient(ellipse 60% 50% at 85% 10%, color-mix(in oklch, var(--brand) 35%, transparent) 0%, transparent 100%),
        radial-gradient(ellipse 70% 60% at 55% 50%, color-mix(in oklch, var(--brand) 18%, transparent) 0%, transparent 100%);
}

.rw-ambient::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom,
    rgb(247, 247, 244) 0%,
    rgba(247, 247, 244, 0.2) 40%,
    rgba(247, 247, 244, 0.8) 75%,
    var(--rw-bg) 100%);
}

.rw-page.is-dark .rw-ambient::after {
    background: linear-gradient(to bottom,
    rgba(15, 17, 21, 0) 0%,
    rgba(15, 17, 21, 0.15) 40%,
    rgba(15, 17, 21, 0.7) 75%,
    var(--rw-bg) 100%);
}


/* Brand tint overlay */
.rw-brand-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--brand);
    opacity: 0.03;
    z-index: 0;
    pointer-events: none;
}

/* ─── Signature panel ────────────────────────────────────────────────────── */
.rw-sig {
    padding: 32px 0 20px;
}

.rw-sig-frame {
    position: relative;
    padding: 44px 48px 32px;
    background: var(--rw-panel-bg, var(--rw-paper));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    border-radius: var(--rw-r-lg);
}


.rw-sig-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: flex-start;
}

.rw-sig-tag {
    font-size: 0.8em;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--rw-ink-3);
}

.rw-sig-title {
    font-family: var(--rw-font-display);
    font-size: 3.6em;
    line-height: 1;
    letter-spacing: -0.03em;
    font-weight: 600;
    color: var(--rw-ink);
    margin: 0;
}

.rw-sig-sub {
    font-size: 1.067em;
    color: var(--rw-ink-2);
    max-width: 580px;
}

.rw-sig-meta {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    width: 100%;
    margin-top: 12px;
    border-top: 1px solid var(--rw-line);
    padding-top: 18px;
    gap: 24px;
}

.rw-sig-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.rw-sig-lbl {
    font-family: var(--rw-font-mono);
    font-size: 0.667em;
    letter-spacing: 0.06em;
    color: var(--rw-ink-3);
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.rw-sig-val {
    font-family: var(--rw-font-display);
    font-size: 1.6em;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--rw-ink);
    line-height: 1.1;
}

.rw-sig-val small {
    font-family: var(--rw-font-body);
    font-size: 0.5em;
    font-weight: 400;
    color: var(--rw-ink-3);
}

/* ─── Filter bar ─────────────────────────────────────────────────────────── */
.rw-filter-shell {
    position: relative;
    z-index: 5;
    padding-top: 8px;
    padding-bottom: 4px;
}

.rw-filter-wrap {
    position: relative;
}

.rw-filter {
    background: var(--rw-panel-bg, var(--rw-paper));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    border: 1px solid var(--rw-line);
    border-radius: var(--rw-r-xl);
    padding: 8px;
    display: grid;
    grid-template-columns: 1.5fr 1fr 1.2fr 1fr auto;
    gap: 6px;
    box-shadow: 0 1px 0 rgba(15, 17, 21, 0.02), 0 18px 40px -24px rgba(15, 17, 21, 0.18);
    align-items: stretch;
}

.rw-fld {
    position: relative;
    padding: 12px 16px;
    border-radius: var(--rw-r-lg);
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 62px;
    transition: background 0.15s;
    border: none;
    background: none;
    color: inherit;
    font-family: inherit;
}

.rw-fld:hover {
    background: var(--rw-bg);
}

.rw-fld.is-active {
    background: var(--rw-brand-tint);
}

.rw-fld + .rw-fld::before {
    content: "";
    position: absolute;
    left: -3px;
    top: 14px;
    bottom: 14px;
    width: 1px;
    background: var(--rw-line-2);
}

.rw-fld-label {
    font-size: 0.733em;
    font-weight: 600;
    color: var(--rw-ink-2);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: var(--rw-font-mono);
    display: flex;
    align-items: center;
    gap: 6px;
    pointer-events: none;
}

.rw-fld-value {
    font-size: 0.933em;
    color: var(--rw-ink);
    margin-top: 4px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
}

.rw-fld-value.is-placeholder {
    color: var(--rw-ink-4);
    font-weight: 400;
}

.rw-fld-input {
    background: transparent;
    border: 0;
    outline: 0;
    padding: 0;
    width: 100%;
    font-family: inherit;
    font-size: 0.933em;
    color: var(--rw-ink);
    cursor: text;
}

.rw-fld-input::placeholder {
    color: var(--rw-ink-4);
    font-weight: 400;
}

.rw-btn-search {
    align-self: stretch;
    background: var(--brand);
    color: #fff;
    border-radius: var(--rw-r-lg);
    padding: 0 22px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    font-size: 0.933em;
    min-width: 120px;
    justify-content: center;
    border: none;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.12s;
}

.rw-btn-search:hover {
    background: var(--rw-brand-2);
}

.rw-loading-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--brand);
    animation: rw-pulse 1s ease-in-out infinite;
}

@keyframes rw-pulse {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1); }
}

/* ─── Popovers ───────────────────────────────────────────────────────────── */
.rw-pop {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    background: var(--rw-panel-bg, var(--rw-paper));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    border: 1px solid var(--rw-line);
    border-radius: var(--rw-r-lg);
    box-shadow: 0 24px 60px -20px rgba(15, 17, 21, 0.25), 0 1px 0 rgba(15, 17, 21, 0.04);
    padding: 20px;
    z-index: 60;
    min-width: 340px;
    text-align: left;
}

.rw-pop-cal {
    width: 660px;
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
}

.rw-pop-cal-fd {
    position: absolute;
    left: 20px;
    right: 20px;
    bottom: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 14px;
    margin-top: 8px;
    border-top: 1px solid var(--rw-line-2);
}

.rw-cal-nights {
    font-size: 0.867em;
    color: var(--rw-ink-2);
}

.rw-cal-nights b {
    font-family: var(--rw-font-display);
    font-weight: 600;
    font-size: 1em;
    color: var(--rw-ink);
}

/* ─── Calendar ───────────────────────────────────────────────────────────── */
.rw-cal-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.rw-cal-hd b {
    font-size: 0.933em;
    font-weight: 600;
}

.rw-cal-nav {
    display: flex;
    gap: 6px;
}

.rw-cal-nav button {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--rw-line);
    color: var(--rw-ink-2);
    background: none;
    cursor: pointer;
    font-family: inherit;
}

.rw-cal-nav button:hover {
    border-color: var(--rw-ink-3);
    color: var(--rw-ink);
}

.rw-pop-cal--refreshing .rw-cal-grid,
.rw-cal-grid--refreshing {
    opacity: 0.4;
    pointer-events: none;
    transition: opacity 0.15s;
}

.rw-cal-nav-btn--refresh {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--rw-line);
    color: var(--rw-ink-3);
    background: none;
    cursor: pointer;
    font-family: inherit;
    flex-shrink: 0;
}

.rw-cal-nav-btn--refresh:hover:not(:disabled) {
    border-color: var(--rw-ink-3);
    color: var(--rw-ink);
}

.rw-cal-nav-btn--refresh:disabled {
    opacity: 0.4;
    cursor: default;
}

@keyframes rw-spin {
    to { transform: rotate(360deg); }
}

.rw-spin {
    animation: rw-spin 0.7s linear infinite;
    display: inline-block;
}

.rw-cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
}

.rw-cal-dow {
    font-size: 0.667em;
    font-family: var(--rw-font-mono);
    color: var(--rw-ink-3);
    text-align: center;
    padding: 6px 0;
    font-weight: 500;
    letter-spacing: 0.04em;
}

.rw-cal-cell {
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.867em;
    border-radius: var(--rw-r-sm);
    color: var(--rw-ink-2);
    position: relative;
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    padding: 0;
}

.rw-cal-cell.out {
    color: var(--rw-ink-4);
    opacity: 0.4;
    pointer-events: none;
}

.rw-cal-cell.out.in-range {
    opacity: 0.25;
    background: var(--rw-brand-tint);
    border-radius: 0;
}

.rw-cal-cell.disabled {
    color: var(--rw-ink-4);
    text-decoration: line-through;
    opacity: 0.5;
    pointer-events: none;
}

.rw-cal-cell.blocked-start,
.rw-cal-cell.blocked-end {
    color: var(--rw-ink-4);
    opacity: 0.4;
    cursor: not-allowed;
    background: repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 3px,
        rgba(0, 0, 0, 0.06) 3px,
        rgba(0, 0, 0, 0.06) 6px
    );
}

.dark .rw-cal-cell.blocked-start,
.dark .rw-cal-cell.blocked-end {
    background: repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 3px,
        rgba(255, 255, 255, 0.06) 3px,
        rgba(255, 255, 255, 0.06) 6px
    );
}

.rw-cell-tip {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: #1a1a1a;
    color: #fff;
    font-size: 0.733em;
    line-height: 1.4;
    white-space: nowrap;
    padding: 4px 8px;
    border-radius: 5px;
    pointer-events: none;
    z-index: 200;
    display: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.25);
}

.rw-cell-tip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #1a1a1a;
}

.rw-cal-cell:hover .rw-cell-tip {
    display: block;
}

.rw-cal-cell:not(.out):not(.disabled):not(.blocked-start):not(.blocked-end):hover {
    background: var(--rw-bg);
}

.rw-cal-cell.in-range {
    background: var(--rw-brand-tint);
    border-radius: 0;
}

.rw-cal-cell.range-start {
    background: var(--brand);
    color: #fff;
    border-radius: var(--rw-r-sm) 0 0 var(--rw-r-sm);
    font-weight: 600;
}

.rw-cal-cell.range-end {
    background: var(--brand);
    color: #fff;
    border-radius: 0 var(--rw-r-sm) var(--rw-r-sm) 0;
    font-weight: 600;
}

.rw-cal-cell.range-single {
    background: var(--brand);
    color: #fff;
    border-radius: var(--rw-r-sm);
    font-weight: 600;
}

.rw-cal-cell.today::after {
    content: "";
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    width: 3px;
    height: 3px;
    border-radius: 999px;
    background: currentColor;
}

.rw-cal-cell.busy-full::before {
    content: "";
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #ef4444;
}

.rw-cal-cell.busy-partial::before {
    content: "";
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #f59e0b;
}

/* ─── Price popover ──────────────────────────────────────────────────────── */
.rw-pop-price {
    width: 340px;
}

.rw-price-pop-h {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 14px;
}

.rw-price-pop-h b {
    font-size: 0.933em;
}

.rw-price-pop-h span {
    color: var(--rw-ink-3);
    font-size: 0.8em;
    font-family: var(--rw-font-mono);
}

.rw-price-hist {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 18px;
    padding: 0 2px;
}

.rw-price-bar {
    flex: 1;
    background: var(--rw-brand-soft);
    border-radius: 2px 2px 0 0;
    min-height: 3px;
}

.rw-price-bar.active {
    background: var(--brand);
}

.rw-price-slider {
    margin: 18px 4px 22px;
}

/* PrimeVue slider override */
.rw-page :deep(.p-slider) {
    background: var(--rw-line);
    height: 4px;
    border-radius: 999px;
}

.rw-page :deep(.p-slider .p-slider-range) {
    background: var(--brand);
    border-radius: 999px;
}

.rw-page :deep(.p-slider .p-slider-handle) {
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #fff;
    border: 2px solid var(--brand);
    box-shadow: 0 2px 6px rgba(15, 17, 21, 0.15);
    margin-top: -9px;
    margin-left: -11px;
}

.rw-price-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.rw-price-input {
    border: 1px solid var(--rw-line);
    border-radius: 12px;
    padding: 10px 12px;
}

.rw-price-input label {
    font-size: 0.667em;
    font-family: var(--rw-font-mono);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--rw-ink-3);
    display: block;
    font-weight: 600;
}

.rw-price-input-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-top: 2px;
}

.rw-price-input input {
    border: 0;
    outline: 0;
    padding: 0;
    width: 100%;
    font-size: 1.067em;
    font-weight: 600;
    background: transparent;
    color: var(--rw-ink);
    font-family: inherit;
}

.rw-price-input span {
    color: var(--rw-ink-3);
    font-size: 0.867em;
}

/* ─── Chips ──────────────────────────────────────────────────────────────── */
.rw-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border-radius: 999px;
    background: var(--rw-paper);
    border: 1px solid var(--rw-line);
    font-size: 0.867em;
    font-weight: 500;
    color: var(--rw-ink-2);
    cursor: pointer;
    font-family: inherit;
}

.rw-chip:hover {
    border-color: var(--rw-ink-4);
}

.rw-chip-on {
    background: var(--rw-ink);
    color: #fff;
    border-color: var(--rw-ink);
}

.rw-chip-x {
    margin-left: 2px;
    font-size: 0.933em;
    opacity: 0.7;
}

/* ─── Results heading ────────────────────────────────────────────────────── */
.rw-main {
    flex: 1;
}

.rw-results-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding: 24px 0 18px;
    gap: 18px;
    flex-wrap: wrap;
}

.rw-eyebrow {
    font-family: var(--rw-font-mono);
    font-size: 0.733em;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--rw-ink-2);
}

.rw-results-h2 {
    font-family: var(--rw-font-display);
    font-size: 1.6em;
    font-weight: 600;
    letter-spacing: -0.015em;
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: 10px;
}

.rw-results-h2 small {
    font-family: var(--rw-font-body);
    font-size: 0.583em;
    font-weight: 400;
    color: var(--rw-ink-3);
}

.rw-results-tools {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

/* ─── Empty state ────────────────────────────────────────────────────────── */
.rw-empty {
    padding: 60px 20px;
    text-align: center;
    border: 1px dashed var(--rw-line);
    border-radius: var(--rw-r-lg);
    background: var(--rw-paper);
    margin-bottom: 40px;
}

.rw-empty h3 {
    font-family: var(--rw-font-display);
    font-size: 1.2em;
    font-weight: 600;
    margin: 0;
}

.rw-empty p {
    color: var(--rw-ink-3);
    font-size: 0.933em;
    margin: 6px 0 0;
}

/* ─── Item grid ──────────────────────────────────────────────────────────── */
.rw-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px 22px;
    padding-bottom: 80px;
    transition: opacity 0.2s;
}

.rw-grid.rw-grid-loading {
    opacity: 0.5;
    pointer-events: none;
}

/* ─── Card ───────────────────────────────────────────────────────────────── */
.rw-card {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    position: relative;
}

.rw-card.is-unavailable {
    opacity: 0.5;
    pointer-events: none;
}

.rw-card-photo {
    position: relative;
    aspect-ratio: 4 / 3;
    border-radius: var(--rw-r-lg);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    overflow: hidden;
    background: var(--rw-line-2);
}

.rw-card-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.rw-card:hover .rw-card-photo {
    transform: translateY(-2px);
    transition: transform 0.2s;
}

.rw-card:hover .rw-card-title {
    color: var(--rw-brand-2);
}


/* Unavailable overlay */
.rw-unavail-overlay {
    position: absolute;
    inset: 0;
    background: rgba(15, 17, 21, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--rw-r-lg);
    z-index: 2;
}

.rw-unavail-overlay span {
    background: rgba(15, 17, 21, 0.7);
    color: #fff;
    padding: 6px 16px;
    border-radius: 999px;
    font-size: 0.867em;
    font-weight: 600;
}

/* Available badge */
.rw-avail-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 3;
    background: #1F8A5B;
    color: #fff;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 0.733em;
    font-weight: 600;
    white-space: nowrap;
}

/* Heart button */
.rw-heart {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.92);
    color: var(--rw-ink-2);
    font-size: 0.933em;
    border: 1px solid rgba(15, 17, 21, 0.06);
    cursor: pointer;
    z-index: 3;
    font-family: inherit;
    transition: background 0.15s;
}

.rw-heart.is-on {
    background: #fff;
    color: var(--brand);
}

.rw-heart:hover {
    background: #fff;
}

/* Card meta */
.rw-card-meta {
    padding: 19px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: var(--rw-panel-bg, var(--rw-paper));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    border-radius: 0 0 var(--rw-r-md) var(--rw-r-md);
}

.rw-card-loc {
    font-size: 0.8em;
    color: var(--rw-ink-3);
    font-family: var(--rw-font-mono);
    letter-spacing: 0.02em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
}

.rw-type-pill {
    padding: 2px 7px;
    border-radius: 4px;
    background: var(--rw-brand-tint);
    color: var(--rw-brand-2);
    font-weight: 600;
    font-size: 0.667em;
}

.rw-card-title {
    font-family: var(--rw-font-display);
    font-size: 1.133em;
    font-weight: 600;
    color: var(--rw-ink);
    letter-spacing: -0.01em;
    line-height: 1.25;
    transition: color 0.15s;
}

.rw-card-feats {
    font-size: 0.867em;
    color: var(--rw-ink-2);
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    align-items: center;
}

.rw-dot {
    width: 3px;
    height: 3px;
    border-radius: 999px;
    background: var(--rw-ink-4);
    flex-shrink: 0;
}

.rw-card-desc {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}

.rw-card-bot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 6px;
    gap: 8px;
}

.rw-card-price {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    font-family: var(--rw-font-display);
    font-size: 1.2em;
    font-weight: 600;
    color: var(--rw-ink);
    letter-spacing: -0.01em;
}

.rw-card-price small {
    font-family: var(--rw-font-body);
    font-size: 0.867em;
    font-weight: 400;
    color: var(--rw-ink-3);
    margin-left: 3px;
}

.rw-card-base {
    font-size: 0.733em;
    font-weight: 400;
    color: var(--rw-ink-3);
    font-family: var(--rw-font-body);
}

.rw-book-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    height: 38px;
    border-radius: 10px;
    background: var(--brand);
    color: #fff;
    font-size: 0.867em;
    font-weight: 600;
    border: none;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.12s;
    flex-shrink: 0;
}

.rw-book-btn:hover {
    background: var(--rw-brand-2);
}

.rw-book-btn:disabled {
    background: var(--rw-line);
    color: var(--rw-ink-4);
    cursor: not-allowed;
}

/* ─── Micro footer ───────────────────────────────────────────────────────── */
.rw-footer {
    padding: 24px 0 40px;
    margin-top: 40px;
    border-top: 1px solid var(--rw-line-2);
}

.rw-footer-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.rw-powered {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--rw-font-mono);
    font-size: 0.733em;
    color: var(--rw-ink-3);
    letter-spacing: 0.06em;
    text-transform: uppercase;
}


.rw-powered-brand {
    font-weight: 700;
    color: var(--brand);
}

.rw-footer-links {
    font-size: 0.8em;
    color: var(--rw-ink-3);
    font-family: var(--rw-font-mono);
    letter-spacing: 0.02em;
}

.rw-footer-links a {
    color: var(--rw-ink-3);
    transition: color 0.15s;
    cursor: pointer;
}

.rw-footer-links a:hover {
    color: var(--rw-ink);
}



/* ─── Detail overlay ────────────────────────────────────────────────────── */
.rw-detail-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: var(--rw-bg);
    overflow-y: auto;
}

.rw-detail-enter-active { animation: rw-detail-in  0.38s cubic-bezier(0.25, 1, 0.3, 1); }
.rw-detail-leave-active { animation: rw-detail-out 0.28s cubic-bezier(0.4, 0, 1, 1); }

@keyframes rw-detail-in {
    from { transform: translateY(48px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
}
@keyframes rw-detail-out {
    from { transform: translateY(0);    opacity: 1; }
    to   { transform: translateY(32px); opacity: 0; }
}
/* ─── Icon ───────────────────────────────────────────────────────────────── */
.rw-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

/* ─── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 1100px) {
    .rw-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .rw-sig-title {
        font-size: 2.933em;
    }
}

@media (max-width: 768px) {
    .rw-shell {
        padding: 0 16px;
    }

    .rw-sig-frame {
        padding: 24px 18px 22px;
    }

    .rw-sig-title {
        font-size: 2.267em;
        letter-spacing: -0.025em;
    }

    .rw-sig-sub {
        font-size: 0.933em;
    }

    .rw-sig-meta {
        grid-template-columns: repeat(2, 1fr);
        gap: 18px 16px;
        padding-top: 16px;
        margin-top: 8px;
    }

    .rw-sig-val {
        font-size: 1.2em;
    }


    .rw-filter {
        grid-template-columns: 1fr;
        gap: 0;
        padding: 6px;
        border-radius: var(--rw-r-lg);
    }

    .rw-fld {
        padding: 12px 14px;
        min-height: auto;
        border-radius: var(--rw-r-md);
    }

    .rw-fld + .rw-fld::before {
        display: none;
    }

    .rw-fld:not(:last-of-type) {
        border-bottom: 1px solid var(--rw-line-2);
    }

    .rw-btn-search {
        margin-top: 6px;
        height: 48px;
        border-radius: 14px;
    }

    .rw-pop {
        position: fixed !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        top: auto !important;
        margin: 0;
        width: 100% !important;
        min-width: 0 !important;
        max-width: 100% !important;
        border-radius: 24px 24px 0 0;
        padding: 20px 16px 28px;
        box-shadow: 0 -20px 60px -10px rgba(15, 17, 21, 0.3);
        animation: rw-slideUp 0.25s cubic-bezier(0.25, 1, 0.3, 1);
    }

    @keyframes rw-slideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }

    .rw-pop-cal {
        flex-direction: column;
        gap: 8px;
    }

    .rw-pop-cal-fd {
        position: static !important;
        flex-direction: column;
        gap: 10px;
        padding-top: 12px;
    }

    .rw-pop-price {
        width: 100% !important;
    }

    .rw-results-head {
        flex-direction: column;
        align-items: flex-start;
        gap: 14px;
        padding: 18px 0 14px;
    }

    .rw-grid {
        grid-template-columns: 1fr;
        gap: 18px;
        padding-bottom: 60px;
    }


}

@media (max-width: 480px) {
    .rw-sig-title {
        font-size: 1.867em;
    }

    .rw-sig-meta {
        grid-template-columns: 1fr 1fr;
    }
}

/* ── Guest selector ─────────────────────────────────────────────────────── */
.rw-guests { display: flex; flex-direction: column; gap: 14px; padding: 4px 0 8px; }
.rw-guest-row { display: flex; justify-content: space-between; align-items: center; }
.rw-guest-lbl { font-size: 0.933em; font-weight: 500; color: var(--rw-ink); }
.rw-guest-sub { font-size: 0.8em; color: var(--rw-ink-3); margin-top: 2px; }
.rw-counter { display: flex; align-items: center; gap: 14px; }
.rw-counter button {
    width: 32px; height: 32px; border-radius: 50%;
    border: 1.5px solid var(--rw-line); background: var(--rw-paper);
    font-size: 1.2em; line-height: 1; cursor: pointer; color: var(--rw-ink);
    display: flex; align-items: center; justify-content: center;
    transition: opacity 0.12s;
}
.rw-counter button:disabled { opacity: 0.3; cursor: default; }
.rw-counter span { font-size: 1.067em; font-weight: 600; min-width: 20px; text-align: center; color: var(--rw-ink); }
.rw-guest-cap { color: var(--rw-ink-3); font-size: 0.8em; margin-top: -4px; }

/* ── Guest filter popover ────────────────────────────────────────────────── */
.rw-pop-guests { min-width: 300px; }

/* ── Card attribute pills ────────────────────────────────────────────────── */
.rw-card-attrs { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
.rw-attr-pill {
    font-size: 0.733em; color: var(--rw-ink-2);
    background: var(--rw-bg); border: 1px solid var(--rw-line);
    border-radius: 20px; padding: 2px 8px;
    display: inline-flex; align-items: center; gap: 4px;
}

</style>
